import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

export default function MilesReport() {
  const [loads, setLoads] = useState([]);
  const [driverFilter, setDriverFilter] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const company = useCompanyInfo();

  useEffect(() => {
    client.entities.Load.list('-delivery_date', 500).then(l => { setLoads(l); setLoading(false); });
  }, []);

  const drivers = [...new Set(loads.map(l => l.assigned_driver_name).filter(Boolean))].sort();

  const filtered = loads.filter(l =>
    inRange(l.delivery_date || l.pickup_date, start, end) &&
    (!driverFilter || l.assigned_driver_name === driverFilter) &&
    l.assigned_driver_email
  );

  const totals = {
    loaded: filtered.reduce((s, l) => s + (l.miles || 0), 0),
    deadhead: filtered.reduce((s, l) => s + (l.deadhead_miles || 0), 0),
  };

  const csvRows = filtered.map(l => ({
    Driver: l.assigned_driver_name || '',
    'Load #': l.load_number || '',
    Origin: l.origin || '',
    Destination: l.destination || '',
    'Loaded Miles': l.miles || 0,
    'Deadhead Miles': l.deadhead_miles || 0,
    'Total Miles': (l.miles || 0) + (l.deadhead_miles || 0),
    Date: l.delivery_date || l.pickup_date || '',
  }));

  const handlePrint = () => {
    const cols = ['Driver', 'Load #', 'Origin', 'Destination', 'Loaded Miles', 'Deadhead Miles', 'Total Miles', 'Date'];
    const dataRows = filtered.map(l => [
      { value: l.assigned_driver_name || '', class: 'bold' }, `#${l.load_number || ''}`, l.origin || '', l.destination || '',
      { value: (l.miles || 0).toLocaleString(), class: 'positive' },
      (l.deadhead_miles || 0).toLocaleString(),
      { value: ((l.miles || 0) + (l.deadhead_miles || 0)).toLocaleString(), class: '' },
      l.delivery_date || l.pickup_date || '—',
    ]);
    const grandTotal = Math.round(totals.loaded) + Math.round(totals.deadhead);
    const totalsRow = ['TOTALS', '', '', '',
      { value: Math.round(totals.loaded).toLocaleString(), class: 'gold' },
      Math.round(totals.deadhead).toLocaleString(),
      { value: grandTotal.toLocaleString(), class: 'gold' },
      '',
    ];
    const cards = [
      { label: 'Loads', value: filtered.length, color: '' },
      { label: 'Loaded Miles', value: Math.round(totals.loaded).toLocaleString(), color: 'positive' },
      { label: 'Deadhead Miles', value: Math.round(totals.deadhead).toLocaleString(), color: 'negative' },
      { label: 'Total Miles', value: grandTotal.toLocaleString(), color: 'gold' },
    ];
    const html = generatePrintHTML('Miles Report', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'miles-report.csv')} exportDisabled={loading || filtered.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground">Driver:</label>
        <select value={driverFilter} onChange={e => setDriverFilter(e.target.value)}
          className="px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs">
          <option value="" className="bg-background">All Drivers</option>
          {drivers.map(d => <option key={d} value={d} className="bg-background">{d}</option>)}
        </select>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Driver', 'Load #', 'Origin', 'Destination', 'Loaded Miles', 'Deadhead Miles', 'Total Miles', 'Date'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map(l => (
              <tr key={l.id} className="hover:bg-white/3">
                <td className="px-3 py-2.5 text-foreground">{l.assigned_driver_name}</td>
                <td className="px-3 py-2.5 text-primary">#{l.load_number}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{l.origin}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{l.destination}</td>
                <td className="px-3 py-2.5 text-foreground">{(l.miles || 0).toLocaleString()}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{(l.deadhead_miles || 0).toLocaleString()}</td>
                <td className="px-3 py-2.5 font-semibold text-foreground">{((l.miles || 0) + (l.deadhead_miles || 0)).toLocaleString()}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{l.delivery_date || l.pickup_date || '—'}</td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="border-t border-border bg-white/5 font-bold">
                <td colSpan={4} className="px-3 py-2.5 text-foreground">TOTALS</td>
                <td className="px-3 py-2.5 text-foreground">{Math.round(totals.loaded).toLocaleString()}</td>
                <td className="px-3 py-2.5 text-foreground">{Math.round(totals.deadhead).toLocaleString()}</td>
                <td className="px-3 py-2.5 text-foreground">{(Math.round(totals.loaded) + Math.round(totals.deadhead)).toLocaleString()}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
        {filtered.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No mileage data for this period.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
    </div>
  );
}